import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero } from "../components/site/Page";
import { GuideLinks } from "../components/site/Guides";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";

export const Route = createFileRoute("/gemenskap")({
  loader: async ({ context }) => {
    const data = await safeLoader("sv.gemenskap.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "gemenskap", "sv") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "Gemenskap | Swedish International School";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:locale", content: "sv_SE" },
        ...ogImage("/og/gemenskap-v1.jpg"),
      ],
      links: seoLinks("/gemenskap"),
    };
  },
  component: Page,
});

function Page() {
  const { text, list, paragraphs } = useContent("gemenskap");
  const moments = list("moments");
  const steps = list("welcome_steps");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <section className="mx-auto max-w-6xl px-5 py-16">
        <h2 className="text-2xl font-semibold">{text("moments.title")}</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {moments.map((m) => (
            <div key={m.title} className="rounded-xl border border-border bg-card p-6 shadow-soft">
              <h3 className="text-base font-semibold">{m.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-sand">
        <div className="mx-auto max-w-4xl px-5 py-16">
          <h2 className="text-2xl font-semibold">{text("welcome.title")}</h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {text("welcome.intro")}
          </p>
          <ol className="mt-8 space-y-5">
            {steps.map((s, i) => (
              <li key={s.title} className="flex gap-5 rounded-xl bg-card p-6 shadow-soft">
                <span className="font-display text-2xl font-semibold text-primary">{i + 1}</span>
                <div>
                  <h3 className="text-lg font-semibold">{s.title}</h3>
                  {s.text ? (
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16 text-base leading-relaxed text-foreground/90">
        <h2 className="text-2xl font-semibold">{text("arrange.title")}</h2>
        {paragraphs("arrange.text").map((p) => (
          <p key={p} className="mt-4">
            {p}
          </p>
        ))}
        <p className="mt-3">
          <a className="text-primary link-quiet" href="mailto:info@sweintschool.com">
            info@sweintschool.com
          </a>
        </p>
      </section>

      <GuideLinks title={text("guides.title")} />
      <div className="mx-auto max-w-3xl space-y-6 px-5 pb-4 text-base leading-relaxed text-foreground/90">
        <h2 className="pt-4 text-2xl font-semibold">{text("why.title")}</h2>
        {paragraphs("why.text").map((p) => (
          <p key={p}>{p}</p>
        ))}

        <h2 className="pt-4 text-2xl font-semibold">{text("languages.title")}</h2>
        {paragraphs("languages.text").map((p) => (
          <p key={p}>{p}</p>
        ))}

        <h2 className="pt-4 text-2xl font-semibold">{text("visit.title")}</h2>
        {paragraphs("visit.text").map((p) => (
          <p key={p}>{p}</p>
        ))}
        <p>
          <a className="text-primary link-quiet" href="/kontakt">
            Kontakta oss
          </a>{" "}
          eller läs våra{" "}
          <a className="text-primary link-quiet" href="/guider">
            guider
          </a>{" "}
          inför flytten.
        </p>
      </div>

      <CtaBand title={text("cta.title")} text={text("cta.text")} />
      <div className="h-20" />
    </>
  );
}
